import React, {useEffect, useMemo, useState} from 'react';
import {Navigate, useNavigate, useSearchParams} from "react-router-dom";
import {enqueueSnackbar} from "notistack";
import {isAuthRedirectError, requireAuth} from "../../shared/services/auth.service";
import {logError} from "../../shared/services/logger.service";
import {useToolbar} from "../../components/ToolbarContext";
import {EquipmentDto} from "./dtos/equipment.dto";
import {GearItemDto} from "./dtos/gear.dto";
import {getAllEquipment} from "./services/equipment.service";
import {getAllGear} from "./services/gear.service";
import EquipmentProfilePage from "./profile/EquipmentProfilePage";
import {ListWithRecord, RecordPage, useRecordRoute} from "../../shared/components/record/RecordKit";
import {usePermissions} from "../../shared/permissions/PermissionContext";
import {PermissionCode} from "../../shared/permissions/permissions";
import {
  CardSpec,
  cardSwitchPoints,
  LabeledOutlineBox,
  ListBody,
  ListPageScroll,
  ListPageTitleRow,
  ListRow,
  useContentWidth,
} from "../../shared/components/list-page/ListPageKit";
import {formatHourlyRate, formatMoney} from "../../shared/components/list-page/list-format";

/*
 * Realistic widths. Shippy's Tree Service has four machines and no tools or
 * gear, too few to judge by, so these come from the catalogs the Android
 * pickers offer (backend/Accusite/Api/Catalog/equipment-catalog.json and
 * GearCatalog.kt), measured in Chrome with the card's own CSS:
 *
 * Equipment
 *   title     214px at 18px  "Stump grinder attachment", the longest kind (a
 *                             machine without a nickname is titled by its kind)
 *   kind      168px at 14px  the same, as a line
 *   make      239px at 14px  "Timberwolf TW 280FTR Hybrid · 2024" (98th
 *                             percentile of the 1,289 catalog brand + model)
 *   hidden    147px at 14px  "Hidden from estimates"
 *   rate      123px at 14px  "Rate No hourly rate"
 *   minimum   155px at 14px  "Minimum charge $1,888"
 *   status     88px          the "Finish setup" pill
 * Switch points (cardSwitchPoints): one row from 1,336px, grouped from 714px.
 *
 * Tools & Gear
 *   title     194px at 18px  "Ground protection mats", the longest catalog item
 *   item      152px at 14px  the same, as a line
 *   make      207px at 14px  "Milwaukee M18 Fuel Top Handle"
 *   location  210px at 14px  "Where it lives Trailer 2 back shelf" (free text;
 *                             no real values yet, a plausible long one)
 * Switch points (cardSwitchPoints): one row from 893px, grouped from 486px.
 */
const EQUIPMENT_SPEC: CardSpec = {
  title: 214,
  status: 88,
  slots: [
    {key: 'kind', group: 'details', width: 168},
    {key: 'make', group: 'details', width: 239},
    {key: 'hidden', group: 'details', width: 147},
    {key: 'rate', group: 'money', width: 123},
    {key: 'minimum', group: 'money', width: 155},
  ],
};

const GEAR_SPEC: CardSpec = {
  title: 194,
  status: 0,
  slots: [
    {key: 'item', group: 'details', width: 152},
    {key: 'make', group: 'details', width: 207},
    {key: 'location', group: 'details', width: 210},
  ],
};

/** Below this page width a list's cards are stacked, as on a phone. */
const PHONE_BELOW: Record<Tab, number> = {
  equipment: cardSwitchPoints(EQUIPMENT_SPEC).grouped,
  gear: cardSwitchPoints(GEAR_SPEC).grouped,
};

/** The catalog's labels for its internal type names (Android's equipmentKindLabel). */
const kindLabel = (type: string): string => ({
  'Standard crane': 'Telescopic crane',
  'Knuckleboom crane': 'Knuckle-boom crane',
  'Skid steer / compact track loader': 'Skid steer',
} as Record<string, string>)[type] ?? type;

const trimmed = (value?: string | null) => value?.trim() || undefined;

type Tab = 'equipment' | 'gear';

const TABS: {tab: Tab; label: string}[] = [
  {tab: 'equipment', label: 'Equipment'},
  {tab: 'gear', label: 'Tools & Gear'},
];

/**
 * The Equipment page, as on Android (SQ-441): one title, the Show switch
 * between the company's equipment and its tools & gear, a count with the Add
 * button, and the cards. A machine opens its profile as its own page
 * (/equipment/12) over the list (SQ-574 rule 2), and + Add Equipment opens
 * the catalog's first step (/equipment/new; SQ-592); the web has no tools &
 * gear editor yet, so those cards are read-only.
 */
function EquipmentListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams()
  const record = useRecordRoute('/equipment');
  const openRecord = record.open;
  const toolbarContext = useToolbar();
  const {hasPermission} = usePermissions();

  const [equipment, setEquipment] = useState<EquipmentDto[]>([]);
  const [gear, setGear] = useState<GearItemDto[]>([]);
  const [tab, setTab] = useState<Tab>('equipment');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countRowRef, countRowWidth] = useContentWidth<HTMLDivElement>();

  useEffect(() => {
    toolbarContext.initToolbar('EquipmentListPage', '');
    void fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** `quiet`: a refresh after a save or delete keeps the cards up, so the list is where it was left. */
  async function fetchAll(quiet = false) {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      await requireAuth(navigate);
      const [equipmentRows, gearRows] = await Promise.all([getAllEquipment(navigate), getAllGear(navigate)]);
      setEquipment(equipmentRows);
      setGear(gearRows);
    } catch (e) {
      if (isAuthRedirectError(e)) return;
      logError('Error fetching equipment', {}, e as Error);
      setError(`Couldn't load equipment: ${e}`);
    } finally {
      setLoading(false);
    }
  }

  const equipmentRows: ListRow[] = useMemo(() => equipment.map(machine => {
    // Title: the nickname, else what the machine is; brand and model underneath.
    const kind = trimmed(machine.type) && kindLabel(machine.type.trim());
    const title = trimmed(machine.nickname) ?? kind
      ?? ([machine.brand, machine.model].filter(Boolean).join(' ').trim() || `Equipment #${machine.id}`);
    const brandModel = [machine.brand, machine.model].filter(Boolean).join(' ').trim();
    const make = [brandModel && brandModel !== title ? brandModel : undefined, machine.year?.toString()]
      .filter(Boolean).join(' · ');
    const hasRate = machine.hourlyRate != null && machine.hourlyRate > 0;
    return {
      key: `e${machine.id}`,
      title,
      values: {
        kind: kind == null ? 'Type not set' : kind !== title ? kind : undefined,
        make: make || undefined,
        hidden: machine.showInEstimates === false ? 'Hidden from estimates' : undefined,
        rate: {label: 'Rate', value: hasRate ? formatHourlyRate(machine.hourlyRate!) : 'No hourly rate', muted: machine.hourlyRate == null},
        minimum: {
          label: 'Minimum charge',
          value: machine.minimumCharge > 0 ? formatMoney(machine.minimumCharge) : 'None',
          muted: !(machine.minimumCharge > 0),
        },
      },
      // Set up once it has a catalog category (Android's isSetUp).
      status: trimmed(machine.category) ? undefined : 'Finish setup',
      onClick: () => openRecord(machine.id),
    };
  }), [equipment, openRecord]);

  const gearRows: ListRow[] = useMemo(() => gear.map(item => {
    // The nickname, else what it is; brand and model underneath.
    const what = trimmed(item.item);
    const brandModel = [item.brand, item.model].filter(Boolean).join(' ').trim();
    const title = trimmed(item.nickname) ?? what ?? (brandModel || `Item #${item.id}`);
    const same = (value: string | undefined) => !value || value.toLowerCase() === title.toLowerCase();
    return {
      key: `g${item.id}`,
      title,
      values: {
        item: same(what) ? undefined : what,
        make: same(brandModel) ? undefined : brandModel,
        location: trimmed(item.location)
          ? {label: 'Where it lives', value: item.location!.trim()}
          : {label: 'Where it lives', value: 'Not set', muted: true},
      },
    };
  }), [gear]);

  const onEquipment = tab === 'equipment';
  // Phone width (the shown list's cards are stacked): the count with the Add
  // button at the far right, as on Android. Wider, the button sits at the
  // left, under the Show box's bottom left corner, and the count follows it.
  const phone = countRowWidth < PHONE_BELOW[tab];
  const count = onEquipment ? equipment.length : gear.length;
  const countText = onEquipment
    ? (count === 1 ? '1 piece' : `${count} pieces`)
    : (count === 1 ? '1 item' : `${count} items`);

  // Links from before records had pages: /equipment?equipmentId=12.
  const legacyId = searchParams.get("equipmentId");
  if (legacyId) return <Navigate to={`/equipment/${legacyId.toLowerCase()}`} replace/>;

  return (
    <ListWithRecord
      list={<ListPageScroll>
        {/* Web only: the old menu's Equipment submenu was the one way to this page (SQ-554). */}
        <ListPageTitleRow
          title="Equipment & Tools"
          style={{marginBottom: 6}}
        />
        <LabeledOutlineBox label="Show" style={{marginBottom: 12}}>
          <div className="lp-switch" role="tablist">
            {TABS.map(option => (
              <button
                key={option.tab}
                type="button"
                role="tab"
                aria-selected={tab === option.tab}
                className={tab === option.tab ? 'lp-switch-option lp-switch-option--on' : 'lp-switch-option'}
                onClick={() => setTab(option.tab)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </LabeledOutlineBox>
        <div ref={countRowRef} className={phone ? 'lp-count-row lp-count-row--phone' : 'lp-count-row lp-count-row--wide'}>
          <span className="lp-count">{loading ? '' : countText}</span>
          {hasPermission(PermissionCode.EquipmentCreate) && (onEquipment ? (
            <button type="button" className="lp-button lp-button--tinted" onClick={() => record.open('NEW')}>
              + Add Equipment
            </button>
          ) : (
            // The web has no tools & gear editor yet; the button holds its place.
            <button
              type="button"
              className="lp-button lp-button--tinted"
              onClick={() => enqueueSnackbar('Adding tools & gear on the web is coming soon.', {variant: 'info', autoHideDuration: 4000})}
            >
              + Add Tools & Gear
            </button>
          ))}
        </div>
        <ListBody
          loading={loading}
          error={error}
          onRetry={() => void fetchAll()}
          rows={onEquipment ? equipmentRows : gearRows}
          spec={onEquipment ? EQUIPMENT_SPEC : GEAR_SPEC}
          query=""
          emptyNote={onEquipment
            ? 'No equipment yet. Use + Add Equipment to add your first piece.'
            : 'No tools or gear yet. They are added from the Android app for now.'}
        />
      </ListPageScroll>}
      record={record.id !== undefined && (
        <RecordPage key={record.id} name="EquipmentPage">
          <EquipmentProfilePage
            equipmentId={record.id === 'NEW' ? undefined : record.id}
            onClose={record.close}
            onChanged={() => void fetchAll(true)}
          />
        </RecordPage>
      )}
    />
  );
}

export default EquipmentListPage;
